import { useCallback, useEffect } from 'react';
import type { QueryFunctionOptions } from '@apollo/client';
import { useCursorPagination } from '@frameio/graphql-client';
import type {
  GetDirectMembershipsQuery,
  GetDirectMembershipsQueryVariables,
  Membership,
  Resource,
} from '@frameio/graphql-client/generated';
import { useGetDirectMembershipsQuery } from '@frameio/graphql-client/generated';
import { intl } from '@frameio/i18n';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { toSupportedLocaleOrDefault } from '@frameio/shared/utils/toSupportedLocale';
import { DEFAULT_INITIAL_PAGE_SIZE } from '@frameio/utils';

type Options = QueryFunctionOptions<
  GetDirectMembershipsQuery,
  GetDirectMembershipsQueryVariables
>;
type GetDirectMembershipsArgs = {
  count?: number;
  fetchData?: boolean;
  searchTerm?: string;
  resourceId?: Resource['id'];
  /** Called once each time a new (non-cached-repeat) result arrives,
   * mirroring `useQuery`'s removed `onCompleted` callback. */
  onCompleted?: (data: GetDirectMembershipsQuery) => void;
} & Omit<Options, 'skip' | 'variables' | 'onCompleted'>;

export const useGetDirectMemberships = ({
  count = DEFAULT_INITIAL_PAGE_SIZE,
  fetchData = false,
  searchTerm,
  resourceId,
  onCompleted,
  ...queryOptions
}: GetDirectMembershipsArgs) => {
  const locale = toSupportedLocaleOrDefault(intl().locale);
  const accountId = useActiveAccountId();

  const { data, error, fetchNext, loading, previousData, variables } =
    useCursorPagination(
      useGetDirectMembershipsQuery({
        skip: !fetchData || !resourceId || !accountId,
        variables: {
          accountId: accountId ?? '',
          page: {
            first: count,
          },
          searchTerm: searchTerm || undefined,
          resourceId,
          locale,
        },
        ...queryOptions,
      })
    );

  useEffect(() => {
    if (data) onCompleted?.(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only fire when a new result arrives, not on every `onCompleted` identity change
  }, [data]);

  const source = data ?? previousData;
  const pageInfo = data?.directMemberships?.pageInfo;
  const uniqueMemberCount = data?.account?.resource?.uniqueMemberCount;

  const fetchNextPage = useCallback(async (): Promise<
    Array<Membership['id']>
  > => {
    if (pageInfo?.hasNextPage) {
      const result = await fetchNext({
        count,
        pageInfo,
      });

      return result?.data?.directMemberships?.nodes?.map(({ id }) => id) ?? [];
    }

    return [];
  }, [count, fetchNext, pageInfo]);

  return {
    data,
    directMemberships: source?.directMemberships,
    indirectMemberships: source?.indirectMemberships,
    error,
    fetchNextPage,
    loading,
    uniqueMemberCount,
    variables,
  };
};
